// External feed
// --------------------------------------------------------
import Vue from 'vue'
import api from '@/lib/api'
import utils from './landscape/utils'
import {GA_EVENTS} from '@/lib/ga'

const feedsPerPage = 100
let currentRequestId = null
let sourcesLoadedAt = 0
const API_THRESHOLD = 15 * 60 * 1000
const state = {
  feeds: [],
  totalFeeds: 0,
  sources: null, // Dictionary
  currentFeed: null,
  nextPage: null,
  landscapeSources: null,
  lastFeedDismissed: null,
  newSources: false,
  globalFilters: {}
}

const getters = {
  newSources: state => state.newSources,
  currentFeed: state => state.currentFeed,
  feeds: state => state.feeds,
  totalFeeds: state => state.totalFeeds,
  sources: state => state.sources, // sources related to currentFeed
  getSourceById: state => feedId => state.sources[feedId],
  canLoadFeeds: state => !!state.nextPage,
  landscapeSourcesNoActor: state => state.landscapeSources,
  landscapeSources: (state, getters) => {
    // all sources but filter by current actor
    if (!state.landscapeSources) return null
    const actor = getters.selectedActor
    if (!actor) {
      return state.landscapeSources
    } else {
      return state.landscapeSources.filter(source =>
        source.parentsId.find(actorId => actor._id === actorId)
      )
    }
  },
  getLandscapeSourceById: state => sourceId =>
    state.landscapeSources.find(source => source._id === sourceId),
  globalFilters: state => state.globalFilters
}

const mutations = {
  setNewSources(state, value) {
    state.newSources = value
  },
  setFeeds(state, data) {
    state.feeds = state.feeds.concat(data.feeds)
    state.totalFeeds = data._metadata.total
    state.nextPage = data._metadata.links.next
    state.sources = data.sources
  },
  clearFeeds(state) {
    state.feeds = []
    state.totalFeeds = 0
    state.sources = null
    state.currentFeed = null
    state.nextPage = null
  },
  setCurrentFeed(state, item) {
    state.currentFeed = item
  },
  updateCurrentFeed(state, newItem) {
    state.currentFeed.guid = newItem.guid
    state.currentFeed.description = newItem.description
  },
  removeFeed(state, feedId) {
    let index = state.feeds.findIndex(feed => feed._id === feedId)
    const arrayFeeds = state.feeds.splice(index, 1)
    state.lastFeedDismissed = arrayFeeds[0]
  },
  restoreFeed(state) {
    if (state.lastFeedDismissed) {
      state.feeds.push(state.lastFeedDismissed)
      state.lastFeedDismissed = null
    }
  },
  setLandscapeSources(state, sources) {
    sourcesLoadedAt = Date.now()
    state.landscapeSources = sources
  },
  // Landscape SOURCES
  addSource(state, source) {
    state.landscapeSources.push(source)
  },
  updateSource(state, source) {
    let index = state.landscapeSources.findIndex(s => s._id === source._id)
    state.landscapeSources.splice(index, 1, source)
  },
  removeSource(state, sourceId) {
    let index = state.landscapeSources.findIndex(s => s._id === sourceId)
    state.landscapeSources.splice(index, 1)
  },
  setGlobalFilters(state, filters) {
    if (!filters) {
      state.globalFilters = {}
      return
    }
    Vue.set(state.globalFilters, 'includedKeywords', filters.includedKeywords)
    Vue.set(state.globalFilters, 'excludedKeywords', filters.excludedKeywords)
  }
}

const actions = {
  updateNewSources: ({commit}, {sources}) => {
    commit('setNewSources', new Date(sources) > sourcesLoadedAt)
  },
  clearFeeds: ({commit}) => {
    commit('clearFeeds')
  },
  setCurrentFeed({commit}, item) {
    commit('setCurrentFeed', item)
  },
  loadFeeds: ({state, commit, getters}, payload) => {
    let endpoint = state.nextPage
    if (!payload.nextPage) {
      commit('clearFeeds')
      const landscapeId = getters.currentLandscape._id
      const actorId = getters.selectedActor && getters.selectedActor._id
      endpoint = `landscapes/${landscapeId}/`
      if (actorId) endpoint += `actors/${actorId}/`
      endpoint += `feeds?per_page=${feedsPerPage}`
      if (payload.sort) endpoint += `&sort=${payload.sort}`
      endpoint += '&source=' + payload.sources.join('&source=')
      endpoint += '&type=' + payload.types.join('&type=')
    }
    currentRequestId = Date.now()
    const config = {headers: {'Request-Id': currentRequestId}}
    return api.call('get', endpoint, config).then(response => {
      // Only commit last request
      if (currentRequestId.toString() === response.requestId) {
        utils.parseFeeds(response.feeds, response.sources, getters)
        commit('setFeeds', response)
      }
    })
  },
  loadFullFeed: ({commit, getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    return api
      .call(
        'get',
        `landscapes/${landscapeId}/feeds/${payload.source}/items/${payload.feedId}`
      )
      .then(response => {
        commit('updateCurrentFeed', response)
        return response
      })
  },
  feedToTopic: ({commit, getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    return api
      .call(
        'post',
        `/landscapes/${landscapeId}/actors/${payload.actorId}/feeds/toTopic`,
        payload
      )
      .then(response => {
        commit('removeFeed', payload.id)
        return response
      })
  },
  dismissFeed: ({commit, getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    return api
      .call('post', `landscapes/${landscapeId}/feeds/dismiss`, {
        feedId: payload.source,
        itemId: payload.feedId,
        guid: payload.guid
      })
      .then(() => {
        commit('removeFeed', payload.feedId)
      })
  },
  undismissFeed: ({commit, getters}, payload) => {
    commit('restoreFeed') // Optimistic
    const landscapeId = getters.currentLandscape._id
    return api.call('post', `landscapes/${landscapeId}/feeds/undismiss`, {
      feedId: payload.source,
      itemId: payload.feedId
    })
  },
  // S O U R C E S
  loadSources: ({commit, getters}, payload) => {
    if (
      (payload && payload.force) ||
      sourcesLoadedAt - Date.now() > API_THRESHOLD
    ) {
      const landscapeId = getters.currentLandscape._id
      return api
        .call('get', `/landscapes/${landscapeId}/feeds/sources`)
        .then(response => {
          utils.parseFeedSources(response, getters)
          commit('setNewSources', false)
          commit('setLandscapeSources', response)
        })
    }
  },
  checkSource: ({getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    return api.call(
      'post',
      `landscapes/${landscapeId}/feeds/sources/check`,
      payload
    )
  },
  createSource: ({commit, getters}, payload) => {
    Vue.$ga.event(GA_EVENTS.FEEDSOURCE_CREATE)
    const landscapeId = getters.currentLandscape._id
    return api
      .call(
        'post',
        `landscapes/${landscapeId}/actors/${payload.actorId}/feeds/sources`,
        payload
      )
      .then(source => {
        if (source._id) {
          utils.parseFeedSources([source], getters)
          commit('addSource', source)
          commit('updateLandscapeTags', source.tags)
        }
      })
  },
  newsletterSignup: ({commit, getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    return api
      .call(
        'post',
        `landscapes/${landscapeId}/actors/${payload.actorId}/feeds/sources/newsletters/signup`,
        payload
      )
      .then(source => {
        utils.parseFeedSources([source], getters)
        commit('addSource', source)
      })
  },
  globalFilters: ({commit, getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    return api
      .call('put', `landscapes/${landscapeId}/feeds/filters/`, payload)
      .then(() => {
        const filters = {
          includedKeywords: payload.includedKeywords,
          excludedKeywords: payload.excludedKeywords
        }
        commit('setGlobalFilters', filters)
      })
  },
  editSource: ({commit, getters}, payload) => {
    let source = payload.source
    const landscapeId = source.landscapeId
    const actorId = getters.selectedActor
      ? getters.selectedActor._id
      : source.actorId
    delete payload.source
    return api
      .call(
        'put',
        `landscapes/${landscapeId}/actors/${actorId}/feeds/sources/${source._id}`,
        payload
      )
      .then(newSource => {
        if (newSource) {
          utils.parseFeedSources([newSource], getters)
          source = newSource
        } else {
          // its a filter pipe op
          source.includedKeywords = payload.includedKeywords
          source.excludedKeywords = payload.excludedKeywords
        }
        commit('updateSource', source)
        commit('updateLandscapeTags', source.tags)
      })
  },
  previewMutedFeeds: ({getters}, payload) => {
    let source = payload.source
    const landscapeId = getters.currentLandscapeId
    const actorId =
      (getters.selectedActor && getters.selectedActor._id) ||
      (source && source.actorId)
    const url = payload.global
      ? `landscapes/${landscapeId}/feeds`
      : `landscapes/${landscapeId}/actors/${actorId}/feeds/sources/${source._id}`
    delete payload.source
    delete payload.global
    return api.call('post', `${url}/muted`, payload).then(res => res)
  },
  deleteSource: ({commit}, source) => {
    const landscapeId = source.landscapeId
    const actorId = getters.selectedActor
      ? getters.selectedActor._id
      : source.actorId
    return api
      .call(
        'delete',
        `landscapes/${landscapeId}/actors/${actorId}/feeds/sources/${source._id}`
      )
      .then(() => {
        commit('removeSource', source._id)
      })
  },
  // S T A T S
  loadSourcesStats: ({getters}, payload) => {
    const landscapeId = getters.currentLandscape._id
    const actorId = getters.selectedActor && getters.selectedActor._id
    let endpoint = `landscapes/${landscapeId}/`
    if (actorId) endpoint += `actors/${actorId}/`
    return api.call('post', `${endpoint}feeds/sources/stats`, payload)
  },
  // C H A N N E L S
  loadChannels: ({getters}) => {
    if (!getters.currentLandscape) return false
    const landscapeId = getters.currentLandscape._id
    return api
      .call('get', `/landscapes/${landscapeId}/feeds/channels`)
      .then(channels => {
        return channels
      })
  },
  // P I P E S
  loadPipes: ({getters}) => {
    const landscapeId = getters.currentLandscape._id
    return api
      .call('get', `/landscapes/${landscapeId}/feeds/sources/pipes`)
      .then(pipes => {
        return pipes
      })
  },
  // C O N N E C T O R S
  loadAccesoFeeds: ({getters}) => {
    if (!getters.currentLandscape) return false
    const landscapeId = getters.currentLandscape._id
    return api
      .call('get', `landscapes/${landscapeId}/connectors/ACCESO`)
      .then(response => {
        return response
      })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
